import { useQuery } from '@apollo/client'
import { AppBar, Box, Step, StepLabel, Stepper, Toolbar, Typography, Link } from '@mui/material'
import { useState, useEffect, useMemo, useReducer, useCallback } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { mergeDeep, set, setIn, update } from 'timm'
import { animateScroll as scroll } from 'react-scroll'

import { NoMatch } from '..'
import { BlockQuote, CharityLogo, Container, FundLogo, LinearProgress, Stack, Text, Tooltip } from '../../components'
import { queries } from '../../graphql'
import { useAuth } from '../../hooks'
import { useAdvisorPermissions } from '../../hooks/useAdvisorPermissions'
import { ConfirmStep } from './ConfirmStep'
import { DonorInfoStep } from './DonorInfoStep'
import { GiftAmountStep } from './GiftAmountStep'
import { PaymentStep } from './PaymentStep'
import { SignupSuccessModal } from './SignupSuccessModal'
import { LoginModal } from './LoginModal'
import { SuccessStep } from './SuccessStep'

export function GiftStepper() {
  const { charityId, fundId } = useParams()
  const location = useLocation()
  const [{ isLoggedIn }] = useAuth()
  const { isAdvisorImpersonating } = useAdvisorPermissions()

  const isDonatingToFund = !!fundId
  const donationTargetId = isDonatingToFund ? fundId : charityId

  const stepsOptions = {
    'logged-in-not-credit': [{ label: 'Gift Amount' }, { label: 'Payment' }, { label: 'Confirm' }],
    'logged-in-credit': [{ label: 'Gift Amount' }, { label: 'Confirm' }],
    'logged-in-other': [{ label: 'Gift Amount' }],
    'not-logged-in-not-credit': [
      { label: 'Gift Amount' },
      { label: 'Donor Info' },
      { label: 'Payment' },
      { label: 'Confirm' },
    ],
    'not-logged-in-credit': [{ label: 'Gift Amount' }, { label: 'Donor Info' }, { label: 'Confirm' }],
    'not-logged-in-other': [{ label: 'Gift Amount' }, { label: 'Donor Info' }],
    'logged-in-not-credit-new-fund': [
      { label: 'Gift Amount' },
      { label: 'New Fund' },
      { label: 'Payment' },
      { label: 'Confirm' },
    ],
    'logged-in-credit-new-fund': [{ label: 'Gift Amount' }, { label: 'New Fund' }, { label: 'Confirm' }],
    'logged-in-other-new-fund': [{ label: 'Gift Amount' }, { label: 'New Fund' }],
  }
  const stepNumsList = {
    'logged-in-not-credit': [0, 9999, 1, 2],
    'logged-in-credit': [0, 9999, 9999, 1],
    'logged-in-other': [0, 9999, 9999, 9999],
    'not-logged-in-not-credit': [0, 1, 2, 3],
    'not-logged-in-credit': [0, 1, 9999, 2],
    'not-logged-in-other': [0, 1, 9999, 9999],
    'logged-in-not-credit-new-fund': [0, 1, 2, 3],
    'logged-in-credit-new-fund': [0, 1, 9999, 2],
    'logged-in-other-new-fund': [0, 1, 9999, 9999],
  }

  const [steps, setSteps] = useState(stepsOptions['logged-in-credit'])
  const [stepsNum, setStepsNum] = useState(stepNumsList['not-logged-in-credit'])

  const updateSteps = useCallback((newStepKey) => {
    setSteps(stepsOptions[newStepKey])
    setStepsNum(stepNumsList[newStepKey])
  }, [])

  const scrollToTop = () => {
    scroll.scrollToTop({
      duration: 400,
      smooth: 'easeInOutQuart',
    })
  }

  const [{ activeStep, modal, data: stepperState }, dispatchStep] = useReducer(
    (prevStore, { action, modal: updateModal, state: updateState }) => {
      let store = prevStore
      if (updateState) {
        // Handle securities array specially - replace instead of merge
        // because mergeDeep merges arrays by index
        const securities = updateState?.giftDetails?.securities
        let stateToMerge = updateState
        if (securities) {
          store = setIn(store, ['data', 'giftDetails', 'securities'], securities)
          // Remove securities from updateState to avoid double-processing
          const { giftDetails: { securities: _, ...restGiftDetails } = {}, ...restUpdate } = updateState
          stateToMerge = { ...restUpdate, giftDetails: restGiftDetails }
        }
        store = update(store, 'data', (data) => mergeDeep(data, stateToMerge))
      }
      if (updateModal) {
        store = update(store, 'modal', (oldModal) =>
          updateModal == null ? null : mergeDeep(oldModal ?? {}, updateModal)
        )
      }
      let stepChanged = false

      if (action === 'BACK_TO_GIFT_AMOUNT') {
        store = set(store, 'activeStep', 0)
        stepChanged = true
      } else if (action === 'LOGIN_SUCCESS') {
        store = set(store, 'activeStep', 2)
        store = update(store, 'modal', (oldModal) => (oldModal != null ? null : oldModal))
        stepChanged = true
      } else if (action === 'PREV') {
        store = update(store, 'activeStep', (step) => step - 1)
        stepChanged = true
      } else if (action === 'NEXT') {
        store = update(store, 'activeStep', (step) => step + 1)
        stepChanged = true
      }

      if (stepChanged) {
        scrollToTop()
      }

      return store
    },
    useMemo(() => {
      const draftStateKey = isDonatingToFund ? 'draft_fund_donation_state' : 'draft_donation_state'
      const possibleDraftState = JSON.parse(localStorage.getItem(draftStateKey) || 'null')
      const draftState = possibleDraftState?.forTarget === donationTargetId ? possibleDraftState : {}

      return {
        activeStep: 0,
        actionKey: 'not-logged-in-credit',
        modal: null,
        data: {
          giftDetails: draftState.giftDetails,
          payment: {
            paymentMethodType: draftState.payment?.paymentMethodType,
          },
          isDonatingToFund,
        },
      }
    }, [donationTargetId, isDonatingToFund])
  )

  useEffect(() => {
    const draftStateKey = isDonatingToFund ? 'draft_fund_donation_state' : 'draft_donation_state'

    if (stepperState.success) {
      localStorage.removeItem(draftStateKey)
    } else {
      localStorage.setItem(
        draftStateKey,
        JSON.stringify({
          forTarget: donationTargetId,
          ...stepperState,
        })
      )
    }
  }, [donationTargetId, isDonatingToFund, stepperState])

  const { data: { charity } = {}, loading: charityLoading } = useQuery(queries.charities.charity, {
    variables: { charityId: charityId?.toUpperCase() },
    skip: isDonatingToFund,
  })

  const { data: { generosityFund } = {}, loading: genFundLoading } = useQuery(queries.funds.generosityFund, {
    variables: { fundId: Number(fundId) },
    skip: !isDonatingToFund,
  })

  const { data: { getCharityPaymentMethodSettings: charityOverrides } = {}, loading: overridesLoading } = useQuery(
    queries.charities.getCharityPaymentMethodSettings,
    {
      variables: { charityId },
      skip: isDonatingToFund,
    }
  )

  const { data: { me } = {}, loading: profileLoading } = useQuery(queries.user.me, { skip: !isLoggedIn })
  const { data: myFundData, loading: myFundLoading } = useQuery(queries.funds.myFund, { skip: !isLoggedIn })
  const defaultFund = myFundData?.me?.fund

  if (isAdvisorImpersonating) {
    return <NoMatch title="Access Restricted">Your access level does not permit this action.</NoMatch>
  }

  const isLoading =
    (isDonatingToFund ? genFundLoading || myFundLoading : charityLoading || overridesLoading) || profileLoading
  if (isLoading) return <LinearProgress />

  if (isDonatingToFund) {
    if (!generosityFund) return <NoMatch healthCheck title="Generosity Fund not found" />
  } else {
    if (!charity) return <NoMatch healthCheck title="Charity not found" />
    if (!charity.active) return <CharityNotActive charity={charity} />
  }

  const donationTarget = isDonatingToFund ? generosityFund : charity
  const stepProps = {
    isLoggedIn,
    me,
    charityOverrides,
    defaultFund,
    // charity,
    donationTarget,
    donationTargetId,
    stepperState,
    dispatchStep,
    isDonatingToFund,
  }

  let stepContent = (
    <>
      {activeStep === stepsNum[0] && <GiftAmountStep updateSteps={updateSteps} {...stepProps} />}
      {activeStep === stepsNum[1] && <DonorInfoStep {...stepProps} />}
      {activeStep === stepsNum[2] && <PaymentStep {...stepProps} />}
      {activeStep === stepsNum[3] && <ConfirmStep {...stepProps} />}
    </>
  )
  if (stepperState.success) {
    stepContent = <SuccessStep {...stepProps} />
  } else if (modal) {
    if (modal.type === 'SIGNUP_SUCCESS') {
      // currently no use for Signup SuccessModal
      stepContent = <SignupSuccessModal {...stepProps} params={modal.params} />
    } else if (modal.type === 'LOGIN_MODAL') {
      stepContent = <LoginModal {...stepProps} params={modal.params} />
    } else {
      stepContent = null
    }
  }

  return (
    <>
      {/* Hack to add a blue bar above the main content right below the logo header */}
      <AppBar position="absolute">
        <Toolbar disableGutters />
      </AppBar>
      <AppBar position="sticky" color="default" sx={{ bgcolor: 'white' }}>
        {/* @note This was copied from navbar, but I'm not sure Container is supposed to be used this way */}
        <Container maxWidth="lg" sx={{ my: 0 }}>
          <Toolbar disableGutters>
            {steps.length > 1 && (
              <Stepper activeStep={activeStep} sx={{ flexGrow: 1 }}>
                {steps.map((step, index) => (
                  // eslint-disable-next-line react/no-array-index-key -- Steps are index based
                  <Step key={index}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          my: 2,
          mx: 'auto',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {!stepperState.success &&
          !modal &&
          (isDonatingToFund ? <GenFundHeader generosityFund={generosityFund} /> : <CharityHeader charity={charity} />)}
      </Box>
      {stepContent}

      <Box
        component="footer"
        sx={{ bgcolor: 'grey.300', borderTop: '20px solid', borderColor: 'primary.main', mb: -6 }}
      >
        <Container maxWidth="lg">
          {/* Added footer logo and centered it */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2, pt: 4 }}>
            <Box
              component="img"
              src="/footer-logo.png"
              alt="GiveWise Logo"
              sx={{
                height: 70,
                mb: 1,
                pb: 2,
              }}
            />
            <Typography variant="h6" color="text.primary" textAlign="center">
              Your generosity made easy. Together with{' '}
              <Link
                href="https://www.givewise.ca/gift-processing-powered-givewise"
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  color: 'primary.main',
                  mt: -4,
                }}
              >
                GiveWise
              </Link>
              , we help every gift go further.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  )
}

function CharityHeader({ charity }) {
  return (
    <>
      <Box sx={{ height: 140, m: 1 }}>
        <CharityLogo charityId={charity.id} logo={charity.logo} />
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
        {charity.accountName}
      </Typography>
      <Typography variant="body2">Charity # {charity.id}</Typography>
    </>
  )
}

function GenFundHeader({ generosityFund }) {
  const { data: { getLogo: fund } = {} } = useQuery(queries.funds.getLogo, {
    variables: { fundId: generosityFund.id },
  })
  const myLogo = fund?.logo

  return (
    <>
      {myLogo && (
        <Box sx={{ height: 100, m: 1 }}>
          <FundLogo logo={myLogo} />
        </Box>
      )}
      <Text.H1>
        <small style={{ fontWeight: 'normal' }}>Donate to</small>
        <br />
        {generosityFund?.name}
      </Text.H1>
    </>
  )
}

function CharityNotActive({ charity }) {
  const { emailAddress, contactPhoneNumber, websiteAddress } = charity

  return (
    <Stack>
      <BlockQuote>
        <Text.Body>
          This organization is no longer listed in the GiveWise charity database. Please contact the organization for
          more information.
        </Text.Body>
        {websiteAddress || emailAddress || contactPhoneNumber ? (
          <>
            {websiteAddress && (
              <Text.Body>
                <strong>Website: </strong>{' '}
                <Link
                  href={websiteAddress.startsWith('http') ? websiteAddress : `https://${websiteAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {websiteAddress}
                </Link>
              </Text.Body>
            )}
            {emailAddress && (
              <Text.Body>
                <strong>Email: </strong> <Link href={`mailto:${emailAddress}`}>{emailAddress}</Link>
              </Text.Body>
            )}
            {contactPhoneNumber && (
              <Text.Body>
                <strong>Phone: </strong>{' '}
                <Link href={`tel:+${contactPhoneNumber?.replace('[^0-9]', '')}`}>{contactPhoneNumber}</Link>
              </Text.Body>
            )}
          </>
        ) : (
          <Text.Body>No contact information available</Text.Body>
        )}
      </BlockQuote>
    </Stack>
  )
}
